import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectUserId, selectIsAuthenticated } from '../store/userSlice';
import axios from 'axios';
import { OpenAPI } from '../api/core/OpenAPI';

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export const usePushNotifications = () => {
    const userId = useSelector(selectUserId);
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isSupported, setIsSupported] = useState(false);
    const [permission, setPermission] = useState('default');
    const subscribingRef = useRef(false);

    useEffect(() => {
        const supported = 'serviceWorker' in navigator && 'PushManager' in window;
        setIsSupported(supported);

        if (supported) {
            setPermission(Notification.permission);
        }
    }, []);

    // Check existing subscription on mount
    useEffect(() => {
        if (!isSupported || !isAuthenticated) return;

        navigator.serviceWorker.getRegistration('/sw.js').then((registration) => {
            if (registration) {
                registration.pushManager.getSubscription().then((subscription) => {
                    setIsSubscribed(!!subscription);
                });
            } else {
                setIsSubscribed(false);
            }
        });
    }, [isSupported, isAuthenticated]);

    const subscribe = useCallback(async () => {
        if (!isSupported || !isAuthenticated) return false;
        if (subscribingRef.current) return false;

        subscribingRef.current = true;
        try {
            // Register service worker
            const registration = await navigator.serviceWorker.register('/sw.js');
            await navigator.serviceWorker.ready;

            // Request notification permission
            const perm = await Notification.requestPermission();
            setPermission(perm);

            if (perm !== 'granted') {
                return false;
            }

            // Check if already subscribed
            const existing = await registration.pushManager.getSubscription();
            if (existing) {
                setIsSubscribed(true);
                return true;
            }

            // Get VAPID public key from backend
            const { data } = await axios.get(`${OpenAPI.BASE}/push-subscriptions/vapid-public-key`);
            if (!data.vapidPublicKey) {
                console.error('VAPID public key not configured on server');
                return false;
            }

            // Subscribe to push
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(data.vapidPublicKey)
            });

            // Send subscription to backend
            const subscriptionJson = subscription.toJSON();
            await axios.post(`${OpenAPI.BASE}/push-subscriptions`, {
                endpoint: subscriptionJson.endpoint,
                keys: {
                    p256dh: subscriptionJson.keys.p256dh,
                    auth: subscriptionJson.keys.auth
                }
            }, { withCredentials: true });

            setIsSubscribed(true);
            return true;
        } catch (error) {
            console.error('Failed to subscribe to push notifications:', error);
            return false;
        } finally {
            subscribingRef.current = false;
        }
    }, [isSupported, isAuthenticated]);

    const unsubscribe = useCallback(async () => {
        if (!isSupported) return false;

        try {
            const registration = await navigator.serviceWorker.getRegistration('/sw.js');
            if (registration) {
                const subscription = await registration.pushManager.getSubscription();
                if (subscription) {
                    const endpoint = subscription.endpoint;
                    await subscription.unsubscribe();

                    await axios.delete(`${OpenAPI.BASE}/push-subscriptions`, {
                        data: { endpoint },
                        withCredentials: true
                    });
                }
            }

            setIsSubscribed(false);
            return true;
        } catch (error) {
            console.error('Failed to unsubscribe from push notifications:', error);
            return false;
        }
    }, [isSupported]);

    return {
        isSupported,
        isSubscribed,
        permission,
        subscribe,
        unsubscribe
    };
};
